/**
 * 5b-ii: Price scale transforms (linear | log | percent).
 *
 * Pure functions shared by every Y-projection choke point:
 * - KernelEngine.createYScaleFromDomain (main plot)
 * - createPaneProjector (drawings / hit-test / crosshair / bridges)
 * - AxisGridRenderer.yForValue + ticks (axis labels / gridlines)
 *
 * Invariants:
 * - linear is the default and must remain bit-identical to the legacy math.
 * - log clamps at LOG_CLAMP_MIN and callers fall back to linear when the
 *   domain is non-positive (Q2: fallback + warning, never NaN on canvas).
 * - percent is relative to an explicit base (visible-first close, Q1);
 *   a missing/zero base falls back to linear.
 */
/** Floor for log-domain clamping (Q2). */
export const PRICE_SCALE_LOG_MIN = 1e-6;
function linearRatio(value, min, max) {
    // Legacy parity: span floors at 1e-6 (matches createYScaleFromDomain).
    const span = Math.max(1e-6, max - min);
    return (value - min) / span;
}
function logOf(value) {
    return Math.log(Math.max(value, PRICE_SCALE_LOG_MIN));
}
/**
 * Display-space ratio in [0, 1] (0 = domain min/bottom, 1 = domain max/top).
 * y = top + height - ratio * height (matches legacy yForValue math).
 */
export function scaleValueToRatio(value, domain, spec) {
    if (!Number.isFinite(value)) {
        return 0.5;
    }
    const [rawMin, rawMax] = domain;
    if (!Number.isFinite(rawMin) || !Number.isFinite(rawMax)) {
        return 0.5;
    }
    const mode = spec?.mode ?? "linear";
    let ratio;
    if (mode === "log" && rawMin > 0 && rawMax > 0) {
        const span = logOf(rawMax) - logOf(rawMin);
        if (!(span > 0)) {
            return 0.5;
        }
        ratio = (logOf(value) - logOf(rawMin)) / span;
    }
    else if (mode === "percent") {
        const base = spec?.base;
        if (typeof base === "number" && Number.isFinite(base) && base !== 0) {
            const toPct = (v) => ((v - base) / Math.abs(base)) * 100;
            ratio = linearRatio(toPct(value), toPct(rawMin), toPct(rawMax));
        }
        else {
            ratio = linearRatio(value, rawMin, rawMax);
        }
    }
    else {
        ratio = linearRatio(value, rawMin, rawMax);
    }
    if (spec?.inverted) {
        ratio = 1 - ratio;
    }
    return ratio;
}
/** Inverse of scaleValueToRatio: display-space ratio -> price value. */
export function scaleRatioToValue(ratio, domain, spec) {
    if (!Number.isFinite(ratio)) {
        return NaN;
    }
    const [rawMin, rawMax] = domain;
    if (!Number.isFinite(rawMin) || !Number.isFinite(rawMax)) {
        return NaN;
    }
    const mode = spec?.mode ?? "linear";
    // A3: an inverted scale flips the ratio before the mode math runs, so
    // every mode (linear / log / percent) inverts consistently.
    const r = spec?.inverted ? 1 - ratio : ratio;
    if (mode === "log" && rawMin > 0 && rawMax > 0) {
        const span = logOf(rawMax) - logOf(rawMin);
        if (!(span > 0)) {
            return (rawMin + rawMax) / 2;
        }
        return Math.exp(logOf(rawMin) + r * span);
    }
    if (mode === "percent") {
        const base = spec?.base;
        if (typeof base === "number" && Number.isFinite(base) && base !== 0) {
            const toPct = (v) => ((v - base) / Math.abs(base)) * 100;
            const pctMin = toPct(rawMin);
            const pctMax = toPct(rawMax);
            const pctSpan = pctMax - pctMin;
            if (!(pctSpan > 0) && !(pctSpan < 0)) {
                return base;
            }
            const pct = pctMin + r * pctSpan;
            return base + (pct / 100) * Math.abs(base);
        }
    }
    const span = rawMax - rawMin;
    if (!(span > 0) && !(span < 0)) {
        return (rawMin + rawMax) / 2;
    }
    return rawMin + r * span;
}
/**
 * Log-spaced tick values (1/2/5 x 10^k) inside [min, max].
 * Used instead of the linear calculateTicks when scale mode is log.
 */
export function calculateLogTicks(domain, height) {
    const [rawMin, rawMax] = domain;
    const min = Math.max(rawMin, PRICE_SCALE_LOG_MIN);
    const max = Math.max(rawMax, PRICE_SCALE_LOG_MIN);
    if (!(max > min) || !(height > 0)) {
        return [];
    }
    const targetCount = Math.max(2, Math.min(12, Math.floor(height / 36)));
    const minExp = Math.floor(Math.log10(min));
    const maxExp = Math.ceil(Math.log10(max));
    const candidates = [];
    for (let exp = minExp; exp <= maxExp; exp += 1) {
        const base = Math.pow(10, exp);
        for (const mult of [1, 2, 5]) {
            const tick = mult * base;
            if (tick >= min && tick <= max) {
                candidates.push(tick);
            }
        }
    }
    if (candidates.length <= targetCount) {
        return candidates;
    }
    const step = Math.ceil(candidates.length / targetCount);
    return candidates.filter((_, index) => index % step === 0);
}
